import { Key, useCallback, useState } from 'react';
import { styled } from '@material-ui/core';

import NkHeader from './nkHeader';
import NkRow from './nkRow';
import NkControlPanel from './nkControlPanel/nkControlPanel';

import {
  Columns,
  Data,
  SortingState,
  SortDirection,
  ControlPanelActions,
  CustomCellData,
} from './nkCustomTable.types';
import { NkCustomTableContextProvider } from './nkCustomTable.context';

export interface NkCustomTableProps {
  columns: Columns;
  onSortingChange: (sorting: SortingState) => void;
  onChange?: (dataItem: CustomCellData) => void;
  data?: Data;
  initialSorting?: SortingState;
  emptyDataText?: string;
  controlPanelActions?: ControlPanelActions;
}

export const NkCustomTable = ({
  data,
  columns,
  initialSorting,
  onSortingChange,
  onChange,
  controlPanelActions,
  emptyDataText = 'Data is empty',
}: NkCustomTableProps) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting || {});
  const [selectedRows, setSelectedRows] = useState<Array<Key>>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const columnsLengthWithCheckbox =
    columns.filter(({ isHide }) => !isHide).length + 1;
  const showSelection = !!controlPanelActions?.onDelete

  const isOnDeleteDisabled = selectedRows.length <= 0;

  const handleSortingChange = useCallback(
    (sortKey: string) => () => {
      const newSortOrder: SortDirection =
        sorting[sortKey] === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
      const newSorting: SortingState =
        sorting[sortKey] === SortDirection.DESC
          ? {}
          : { [sortKey]: newSortOrder };

      setSorting(newSorting);
      onSortingChange(newSorting);
    },
    [setSorting, sorting],
  );

  const handleRowCheckboxChange = useCallback(
    (rowIndex: Key) => () => {
      const selectedIndex = selectedRows.indexOf(rowIndex);
      let newSelected = [...selectedRows];

      if (selectedIndex === -1) {
        newSelected.push(rowIndex);
      } else {
        newSelected.splice(selectedIndex, 1);
      }
      setSelectedRows(newSelected);
    },
    [selectedRows, setSelectedRows],
  );

  const handleSelectAllCheckboxChange = useCallback(() => {
    const allSelected = selectedRows.length === data?.length;

    if (allSelected) {
      setSelectedRows([]);
    } else if (data) {
      setSelectedRows(data.map((_, index) => index));
    }
    setIsSelectAll(!allSelected);
  }, [selectedRows, setIsSelectAll, setSelectedRows]);

  return (
    <NkCustomTableContextProvider
      onChange={onChange}
    >
      {controlPanelActions !== undefined ? (
        <NkControlPanel
          columns={columns}
          controlPanelActions={controlPanelActions}
          isOnDeleteDisabled={isOnDeleteDisabled}
          selectedRows={selectedRows}
        />
      ) : null}

      <StyledTable>
        <NkHeader
          showSelection={showSelection}
          columns={columns}
          sorting={sorting}
          onChangeSorting={handleSortingChange}
          onSelectAll={handleSelectAllCheckboxChange}
          isSelectAll={isSelectAll}
        />
        <tbody>
          {data?.length ? (
            data.map((item, rowIndex) => (
              <NkRow
                showSelection={showSelection}
                key={rowIndex}
                item={item}
                columns={columns}
                onSelectRow={handleRowCheckboxChange}
                rowIndex={rowIndex}
                isSelected={selectedRows.includes(rowIndex)}
              />
            ))
          ) : (
            <tr>
              <td colSpan={columnsLengthWithCheckbox}>
                <StyledEmptyData>{emptyDataText}</StyledEmptyData>
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </NkCustomTableContextProvider>
  );
};

const StyledTable = styled('table')(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: theme.customProps.table.fontSize,
}));

const StyledEmptyData = styled('div')({
  textAlign: 'center',
});
